/**
 * Video Upload Component
 * Mobile-responsive video upload with chunked uploads, progress tracking, drag-and-drop, and FERPA compliance
 * Optimized for mobile devices, tablets, and desktops with touch-friendly interfaces
 */

import React, { useState, useRef } from 'react';

import { Upload, X, Play, CheckCircle, AlertCircle } from 'lucide-react';

import {
  evaluationService,
  VideoUpload as VideoUploadType,
  VideoUploadInitResponse,
} from '../../services/evaluationService';
import { Button } from '../common/Button';
import { Card, CardContent, CardHeader } from '../common/Card';

interface VideoUploadProps {
  teacherId: string | number;
  evaluationPurpose: string;
  onUploadComplete: (uploadData: VideoUploadType) => void;
  onCancel: () => void;
}

interface UploadProgress {
  status: 'idle' | 'uploading' | 'completed' | 'error';
  currentChunk: number;
  totalChunks: number;
  uploadedBytes: number;
  totalBytes: number;
  percentage: number;
  error: string | null;
  uploadId?: number;
}

const CHUNK_SIZE = 1024 * 1024;

const VideoUploadComponent: React.FC<VideoUploadProps> = ({
  teacherId,
  evaluationPurpose,
  onUploadComplete,
  onCancel,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    status: 'idle',
    currentChunk: 0,
    totalChunks: 0,
    uploadedBytes: 0,
    totalBytes: 0,
    percentage: 0,
    error: null,
  });
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // File validation
  const validateFile = (file: File): boolean => {
    const maxSize = 500 * 1024 * 1024; // 500MB
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo'];

    if (file.size > maxSize) {
      setUploadProgress(prev => ({
        ...prev,
        status: 'error',
        error: 'File size must be less than 500MB',
      }));
      return false;
    }

    if (!allowedTypes.includes(file.type)) {
      setUploadProgress(prev => ({
        ...prev,
        status: 'error',
        error: 'Please select a valid video file (MP4, MOV, or AVI)',
      }));
      return false;
    }

    return true;
  };

  // Calculate MD5 hash for chunk verification
  const calculateChunkHash = async (chunk: Blob): Promise<string> => {
    const buffer = await chunk.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  // Upload chunks with retry logic
  const uploadChunks = async (
    file: File,
    uploadSession: VideoUploadInitResponse
  ) => {
    const totalChunks = uploadSession.total_chunks;
    const chunkSize = uploadSession.chunk_size;
    let uploadedBytes = 0;

    setUploadProgress(prev => ({
      ...prev,
      status: 'uploading',
      totalChunks,
      totalBytes: file.size,
      error: null,
    }));

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      if (abortControllerRef.current?.signal.aborted) {
        throw new Error('Upload cancelled');
      }

      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      const chunkHash = await calculateChunkHash(chunk);

      try {
        await evaluationService.uploadVideoChunk(
          uploadSession.upload_id,
          chunkIndex,
          chunk,
          chunkHash,
          chunkIndex === totalChunks - 1
        );

        const nextUploadedBytes = uploadedBytes + chunk.size;
        uploadedBytes = nextUploadedBytes;
        const percentage = Math.round(
          (nextUploadedBytes / file.size) * 100
        );

        setUploadProgress(prev => ({
          ...prev,
          currentChunk: chunkIndex + 1,
          uploadedBytes: nextUploadedBytes,
          percentage,
        }));
      } catch (error) {
        console.error(`Chunk ${chunkIndex} upload failed:`, error);
        setUploadProgress(prev => ({
          ...prev,
          status: 'error',
          error: `Upload failed at chunk ${chunkIndex + 1}. Please try again.`,
        }));
        throw error;
      }
    }
  };

  // Main upload function
  const handleUpload = async () => {
    if (!selectedFile || !teacherId) {
      setUploadProgress(prev => ({
        ...prev,
        status: 'error',
        error: 'Missing required information',
      }));
      return;
    }

    if (!validateFile(selectedFile)) {
      return;
    }

    abortControllerRef.current = new AbortController();

    try {
      setUploadProgress(prev => ({
        ...prev,
        status: 'uploading',
        error: null,
      }));

      // Initialize upload session
      const sessionResponse = await evaluationService.initVideoUpload({
        filename: selectedFile.name,
        file_size: selectedFile.size,
        chunk_size: CHUNK_SIZE,
        teacher_id: Number(teacherId),
        evaluation_purpose: evaluationPurpose,
      });

      // Upload file in chunks
      await uploadChunks(selectedFile, sessionResponse);
      const uploadedVideo = await evaluationService.getVideoUpload(
        sessionResponse.upload_id
      );

      setUploadProgress(prev => ({
        ...prev,
        status: 'completed',
        error: null,
      }));

      onUploadComplete(uploadedVideo);
    } catch (error) {
      if (error instanceof Error && error.message === 'Upload cancelled') {
        setUploadProgress(prev => ({
          ...prev,
          status: 'idle',
          error: null,
        }));
      } else {
        setUploadProgress(prev => ({
          ...prev,
          status: 'error',
          error: 'Upload failed. Please try again.',
        }));
      }
    }
  };

  // Cancel upload
  const cancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setUploadProgress(prev => ({
      ...prev,
      status: 'idle',
      error: null,
    }));
  };

  // Reset upload
  const resetUpload = () => {
    setSelectedFile(null);
    setUploadProgress({
      status: 'idle',
      currentChunk: 0,
      totalChunks: 0,
      uploadedBytes: 0,
      totalBytes: 0,
      percentage: 0,
      error: null,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // File selection handlers
  const handleFileSelect = (file: File) => {
    if (validateFile(file)) {
      setSelectedFile(file);
      setUploadProgress(prev => ({
        ...prev,
        status: 'idle',
        error: null,
      }));
    }
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Upload Lesson Video</h3>
          <p className="text-sm text-gray-600">
            Upload a video recording of your lesson for evaluation purposes
          </p>
        </CardHeader>
        <CardContent>
          {!selectedFile ? (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragOver
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                Choose a video file or drag it here
              </h4>
              <p className="text-gray-600 mb-4">
                Supported formats: MP4, MOV, AVI (max 500MB)
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="mb-4"
              >
                Select Video File
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/x-msvideo"
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>
          ) : (
            <div className="space-y-4">
              {/* File info */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Play className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {selectedFile.name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {formatFileSize(selectedFile.size)}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetUpload}
                  disabled={uploadProgress.status === 'uploading'}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Upload progress */}
              {uploadProgress.status === 'uploading' && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Uploading... {uploadProgress.percentage}%</span>
                    <span>
                      Chunk {uploadProgress.currentChunk} of{' '}
                      {uploadProgress.totalChunks}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress.percentage}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Success state */}
              {uploadProgress.status === 'completed' && (
                <div className="flex items-center space-x-2 text-green-600">
                  <CheckCircle className="h-5 w-5" />
                  <span>Upload completed successfully!</span>
                </div>
              )}

              {/* Error state */}
              {uploadProgress.status === 'error' && uploadProgress.error && (
                <div className="flex items-center space-x-2 text-red-600">
                  <AlertCircle className="h-5 w-5" />
                  <span>{uploadProgress.error}</span>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={onCancel}
                  disabled={uploadProgress.status === 'uploading'}
                >
                  Cancel
                </Button>
                {uploadProgress.status === 'idle' ||
                uploadProgress.status === 'error' ? (
                  <Button
                    onClick={() => {
                      setUploadProgress(prev => ({
                        ...prev,
                        status: 'idle',
                        error: null,
                      }));
                      handleUpload();
                    }}
                    className="w-full sm:w-auto min-h-[44px]"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Start Upload
                  </Button>
                ) : uploadProgress.status === 'uploading' ? (
                  <Button variant="outline" onClick={cancelUpload}>
                    <X className="w-4 h-4 mr-2" />
                    Cancel Upload
                  </Button>
                ) : null}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VideoUploadComponent;
